import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFornitore, getDatiAzienda } from '../services/api';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import '../print.css'; // Importa il CSS di stampa

function FornitoriPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fornitore, setFornitore] = useState(null);
  const [datiAzienda, setDatiAzienda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFornitoreAndAziendaData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [fornitoreResponse, aziendaResponse] = await Promise.all([
          getFornitore(id),
          getDatiAzienda()
        ]);
        setFornitore(fornitoreResponse.data);
        setDatiAzienda(aziendaResponse.data);
      } catch (err) {
        setError('Errore nel caricamento dei dati per la stampa.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchFornitoreAndAziendaData();
  }, [id]);

  useEffect(() => {
    const handleAfterPrint = () => {
      // Introduce a small delay to ensure the print dialog is fully dismissed
      setTimeout(() => {
        navigate('/fornitori');
      }, 100);
    };

    if (!loading && !error && fornitore && datiAzienda) {
      window.addEventListener('afterprint', handleAfterPrint);
      window.print();
    }

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [loading, error, fornitore, datiAzienda, navigate]);

  const renderPrintField = (label, value) => (
    <div className="print-field">
      <span className="label">{label}:</span>
      <span className="value">{value || 'N/A'}</span>
    </div>
  );

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Preparazione stampa fornitore...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <p>Impossibile preparare la stampa.</p>
      </Container>
    );
  }

  if (!fornitore || !datiAzienda) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Dati del fornitore o dell'azienda non trovati per la stampa.</Alert>
      </Container>
    );
  }

  return (
    <div id="printable-area" className="portrait-page">
      <div className="print-container">
        <header className="print-header">
          <div className="company-details">
            {datiAzienda.logo_url && (
              <img
                src={`${process.env.REACT_APP_STATIC_FILES_BASE_URL}${datiAzienda.logo_url}`}
                alt="Logo Azienda"
                style={{ maxHeight: '150px', maxWidth: '150px', marginRight: '20px', objectFit: 'contain' }}
              />
            )}
          </div>
          <div className="print-info">
            <p><strong>Data di Stampa:</strong> {new Date().toLocaleDateString()}</p>
            <p><strong>ID Fornitore:</strong> {fornitore.id}</p>
          </div>
        </header>

        <main>
          <h2 className="document-title text-center">Scheda Fornitore</h2>
          <h2 className="document-title text-center">{fornitore.denominazione}</h2>

          {/* Dati Anagrafici */}
          <section className="print-section">
            <div className="print-section-title">Dati Anagrafici</div>
            <div className="print-section-content">
              <Row>
                <Col xs={6}>
                  {renderPrintField('ID', fornitore.id)}
                  {renderPrintField('Denominazione', fornitore.denominazione)}
                  {renderPrintField('Codice Fiscale', fornitore.codice_fiscale)}
                </Col>
                <Col xs={6}>
                  {renderPrintField('Partita IVA', fornitore.partita_iva)}
                  {/* These fields do not exist in FornitoriDetail.js, so they are removed for consistency */}
                  {/* renderPrintField('Luogo di Nascita', fornitore.luogo_nascita) */}
                  {/* renderPrintField('Data di Nascita', fornitore.data_nascita) */}
                </Col>
              </Row>
            </div>
          </section>

          {/* Indirizzo */}
          <section className="print-section">
            <div className="print-section-title">Indirizzo</div>
            <div className="print-section-content">
              <Row>
                <Col xs={6}>
                  {renderPrintField('Indirizzo', fornitore.indirizzo)}
                  {renderPrintField('CAP', fornitore.cap)}
                </Col>
                <Col xs={6}>
                  {renderPrintField('Città', fornitore.citta)}
                  {renderPrintField('Provincia', fornitore.provincia)}
                  {renderPrintField('Nazione', fornitore.nazione)}
                </Col>
              </Row>
            </div>
          </section>

          {/* Contatti */}
          <section className="print-section">
            <div className="print-section-title">Contatti</div>
            <div className="print-section-content">
              <Row>
                <Col xs={6}>
                  {renderPrintField('Email', fornitore.email)}
                  {renderPrintField('PEC', fornitore.pec)}
                </Col>
                <Col xs={6}>
                  {renderPrintField('Telefono', fornitore.telefono)}
                  {renderPrintField('Cellulare', fornitore.cellulare)}
                  {renderPrintField('Codice Destinatario', fornitore.codice_destinatario)}
                </Col>
              </Row>
            </div>
          </section>
        </main>

        <footer className="print-footer">
          <p>{datiAzienda.denominazione} - {datiAzienda.indirizzo} - {datiAzienda.cap} {datiAzienda.citta} ({datiAzienda.provincia}) - P.IVA {datiAzienda.piva}</p>
          <p>Email: {datiAzienda.email} - Tel: {datiAzienda.tel}</p>
        </footer>
      </div>
    </div>
  );
}

export default FornitoriPrint;
